import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  colorOpacityPercent,
  colorWithBaseHex,
  colorWithOpacityPercent,
  normalizeEditableColor,
  toColorInput,
} from "./inspectorModel";

function normalizedFallback(value, fallback = "#000000") {
  const normalized = normalizeEditableColor(value);
  if (normalized && normalized !== "transparent") return normalized;
  return normalizeEditableColor(fallback) || "#000000";
}

function formatSliderValue(value, min, max, unit = "", formatter) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  if (typeof formatter === "function") return formatter(numeric);
  if (Number(min) >= 0 && Number(max) <= 1) return `${Math.round(numeric * 100)}%`;
  return `${numeric}${unit ? ` ${unit}` : ""}`;
}

export function VisualColorControl({
  label,
  value,
  fallback = "#000000",
  disabled = false,
  onChange,
  onReset,
  helperText = "",
  inheritedLabel = "Heredado",
  personalizedLabel = "Personalizado",
  allowTransparent = true,
  allowOpacity = true,
  allowExactCss = false,
  validateExactValue,
  exactValueHelperText = "Admite el formato CSS válido para este campo. Vacío vuelve a heredar.",
  exactValueMaxLength = 100,
  copyHex = false,
}) {
  const own = normalizeEditableColor(value);
  const inherited = !own;
  const transparent = own === "transparent";
  const effective = transparent
    ? normalizedFallback("", fallback)
    : normalizedFallback(own, fallback);
  const pickerHex = toColorInput(effective);
  const opacity = transparent ? 0 : colorOpacityPercent(own || effective, fallback);
  const stateLabel = inherited ? inheritedLabel : transparent ? "Transparente" : personalizedLabel;
  const [hexDraft, setHexDraft] = React.useState(pickerHex.toUpperCase());
  const [hexError, setHexError] = React.useState(false);
  const [exactOpen, setExactOpen] = React.useState(false);
  const [exactDraft, setExactDraft] = React.useState(String(value ?? "").trim());
  const [exactError, setExactError] = React.useState("");
  const [copyFeedback, setCopyFeedback] = React.useState("");
  const copyTimerRef = React.useRef(null);

  React.useEffect(() => {
    setHexDraft(pickerHex.toUpperCase());
    setHexError(false);
  }, [pickerHex, value]);

  React.useEffect(() => {
    setExactDraft(String(value ?? "").trim());
    setExactError("");
  }, [value]);

  React.useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  const commitHex = React.useCallback(() => {
    const candidate = String(hexDraft || "").trim();
    if (!/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(candidate)) {
      setHexError(true);
      setHexDraft(pickerHex.toUpperCase());
      return;
    }
    setHexError(false);
    onChange?.(colorWithBaseHex(transparent ? fallback : (own || effective), candidate, fallback));
  }, [effective, fallback, hexDraft, onChange, own, pickerHex, transparent]);

  const commitExactValue = React.useCallback(() => {
    const candidate = normalizeEditableColor(exactDraft);
    const error = typeof validateExactValue === "function" ? validateExactValue(candidate) : "";
    setExactError(error || "");
    if (error) return;
    if (!candidate && onReset) {
      if (!inherited) onReset();
      return;
    }
    if (candidate === own) return;
    onChange?.(candidate);
  }, [exactDraft, inherited, onChange, onReset, own, validateExactValue]);

  const copyCurrentHex = React.useCallback(async () => {
    if (disabled || !copyHex) return;
    const text = pickerHex.toUpperCase();
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setCopyFeedback("Copiado");
    } catch {
      setCopyFeedback("No se pudo copiar");
    }
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopyFeedback(""), 1800);
  }, [copyHex, disabled, pickerHex]);

  return (
    <Box
      sx={{
        p: 1.05,
        borderRadius: 2.25,
        bgcolor: "rgba(25,36,47,.035)",
        border: "1px solid rgba(25,36,47,.09)",
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
        <Typography variant="body2" sx={{ fontWeight: 900, color: "#19242F", minWidth: 0 }}>
          {label}
        </Typography>
        <Chip size="small" label={stateLabel} variant={inherited ? "outlined" : "filled"} sx={{ flexShrink: 0 }} />
      </Stack>

      {allowExactCss && own && !/^#[0-9a-f]{3,8}$/i.test(own) ? (
        <Typography variant="caption" sx={{ display: "block", mb: 0.7, color: "rgba(25,36,47,.58)", overflowWrap: "anywhere" }}>
          Valor CSS actual: <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 750 }}>{own}</Box>
        </Typography>
      ) : null}

      <Stack direction="row" spacing={0.8} alignItems="flex-start" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
        <Box
          component="input"
          type="color"
          aria-label={`Color de ${label}`}
          value={pickerHex}
          disabled={disabled}
          onChange={(event) => onChange?.(colorWithBaseHex(transparent ? fallback : (own || effective), event.target.value, fallback))}
          sx={{
            width: 48,
            height: 44,
            p: 0.2,
            border: "1px solid rgba(25,36,47,.14)",
            borderRadius: 1.8,
            bgcolor: "transparent",
            cursor: disabled ? "default" : "pointer",
            flexShrink: 0,
            opacity: opacity === 0 ? 0.45 : 1,
          }}
        />
        <TextField
          size="small"
          label="HEX exacto"
          value={hexDraft}
          disabled={disabled}
          error={hexError}
          inputProps={{ maxLength: 7 }}
          helperText={hexError ? "Usá un color HEX válido." : (helperText || (inherited ? "Usa el color heredado hasta que lo personalices." : "Color personalizado."))}
          onFocus={(event) => event.target.select()}
          onChange={(event) => {
            setHexDraft(event.target.value);
            if (hexError) setHexError(false);
          }}
          onBlur={commitHex}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            event.currentTarget.blur();
          }}
          InputProps={copyHex ? {
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copyFeedback || `Copiar ${pickerHex.toUpperCase()}`}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      aria-label={`Copiar HEX de ${label}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={copyCurrentHex}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <ContentCopyRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          } : undefined}
          sx={{ flex: "1 1 180px", minWidth: 0 }}
        />
      </Stack>
      {copyHex && copyFeedback ? (
        <Typography role="status" aria-live="polite" variant="caption" sx={{ display: "block", mt: 0.35, color: "rgba(25,36,47,.62)", fontWeight: 750 }}>
          {copyFeedback}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
        {onReset ? (
          <Button
            size="small"
            disabled={disabled || inherited}
            onClick={onReset}
            sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }}
          >
            Heredar
          </Button>
        ) : null}
        {allowTransparent ? (
          <Button
            size="small"
            disabled={disabled || transparent}
            onClick={() => onChange?.("transparent")}
            sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }}
          >
            Transparente
          </Button>
        ) : null}
      </Stack>

      {allowOpacity ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, minWidth: 0 }}>
          <Typography variant="caption" sx={{ minWidth: 92, color: "rgba(25,36,47,.62)", fontWeight: 850 }}>
            Opacidad {opacity}%
          </Typography>
          <Slider
            size="small"
            min={0}
            max={100}
            step={1}
            value={opacity}
            disabled={disabled}
            valueLabelDisplay="auto"
            valueLabelFormat={(next) => `${next}%`}
            aria-label={`Opacidad de ${label}`}
            onChange={(_, next) => onChange?.(colorWithOpacityPercent(transparent ? fallback : (own || effective), Number(next), fallback))}
            sx={{ flex: 1, minWidth: 80 }}
          />
        </Stack>
      ) : null}

      {allowExactCss ? (
        <Box sx={{ mt: 0.55 }}>
          <Button
            size="small"
            disabled={disabled}
            aria-expanded={exactOpen}
            onClick={() => setExactOpen((open) => !open)}
            sx={{ px: 0.5, minHeight: 44, textTransform: "none", fontWeight: 800, color: "text.secondary" }}
          >
            {exactOpen ? "Ocultar valor CSS" : "Valor CSS (avanzado)"}
          </Button>
          {exactOpen ? (
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 0.4 }}
              label="Valor CSS exacto"
              value={exactDraft}
              disabled={disabled}
              error={Boolean(exactError)}
              helperText={exactError || exactValueHelperText}
              inputProps={{ maxLength: exactValueMaxLength }}
              onChange={(event) => {
                setExactDraft(event.target.value);
                if (exactError) setExactError("");
              }}
              onBlur={commitExactValue}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
                if (event.key === "Escape") {
                  setExactDraft(String(value ?? "").trim());
                  setExactError("");
                }
              }}
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export function VisualSliderControl({
  label,
  value,
  min = 0,
  max = 100,
  step = 1,
  unit = "px",
  disabled = false,
  onChange,
  helperText = "",
  nullable = false,
  fallbackValue,
  onReset,
  resetLabel = "Heredar",
  inheritedLabel = "Heredado",
  valueLabelFormatter,
}) {
  const emptyValue = value === "" || value === null || value === undefined;
  const inherited = nullable && emptyValue;
  const numeric = Number(inherited ? fallbackValue : value);
  const fallback = Number.isFinite(numeric) ? numeric : Number(min) || 0;
  const safeValue = Math.min(Number(max), Math.max(Number(min), fallback));
  const displayUnit = Number(min) >= 0 && Number(max) <= 1 ? "" : unit;
  const formatted = formatSliderValue(safeValue, min, max, displayUnit, valueLabelFormatter);

  return (
    <Box sx={{ p: 1.05, borderRadius: 2.25, bgcolor: "rgba(25,36,47,.035)", border: "1px solid rgba(25,36,47,.09)", minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="body2" sx={{ fontWeight: 900, color: "#19242F", minWidth: 0 }}>{label}</Typography>
        <Chip size="small" variant="outlined" label={inherited ? `${inheritedLabel} · ${formatted}` : formatted} sx={{ flexShrink: 0 }} />
      </Stack>
      <Slider
        size="small"
        min={Number(min)}
        max={Number(max)}
        step={Number(step) || 1}
        value={safeValue}
        disabled={disabled}
        valueLabelDisplay="auto"
        valueLabelFormat={(next) => formatSliderValue(next, min, max, displayUnit, valueLabelFormatter)}
        aria-label={label}
        onChange={(_, next) => onChange?.(Number(next))}
        sx={{ mt: 0.6 }}
      />
      {nullable && onReset ? (
        <Button
          size="small"
          disabled={disabled || inherited}
          onClick={onReset}
          sx={{ minHeight: 44, px: 0.5, textTransform: "none", fontWeight: 850 }}
        >
          {resetLabel}
        </Button>
      ) : null}
      {helperText ? (
        <Typography variant="caption" sx={{ display: "block", mt: nullable && onReset ? 0.1 : -0.2, color: "rgba(25,36,47,.56)" }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}
